import * as React from 'react';
import { useBooleanState } from './useBooleanState';

/**
 * Hook to tracks if focus is on a child element of a given element.
 *
 * The return value contains two flags that indicates if focus is inside the root element
 * and the functions that should be attached to onFocusCapture and onBlurCapture in
 * order to track the state of child focus.
 *
 * The hasFocusRef and hasFocusState both track the same variable, but hasFocusRef is always
 * up to date while hasFocusState gets memoized as part of other hooks. So if you need to
 * have state change as a result of focus changing, use hasFocusState. If you need
 * to always have the correct value of hasFocus during memoized functions (like during onFocus callbacks),
 * use hasFocusRef
 */
export function useInnerFocusTracker(rootRef: React.RefObject<HTMLElement>) {
    const hasFocusRef = React.useRef<boolean>(false);
    const [hasFocusState, setHasFocusState, resetHasFocusState] = useBooleanState(false);
    const setHasFocus = React.useCallback(() => {
        setHasFocusState();
        hasFocusRef.current = true;
    }, []);
    const resetHasFocus = React.useCallback(() => {
        resetHasFocusState();
        hasFocusRef.current = false;
    }, []);
    const onFocusCapture = React.useCallback(() => setHasFocus(), []);
    const onBlurCapture = React.useCallback(
        (e: React.FocusEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.relatedTarget as Node)) {
                resetHasFocus();
            }
        },
        [rootRef.current]
    );

    return {
        hasFocusRef,
        hasFocusState,
        setHasFocus,
        resetHasFocus,
        onFocusCapture,
        onBlurCapture,
    };
}
